/**
 * Pure lane assignment for concurrent events — the part of the week grid
 * (Unit 5) most likely to be wrong and cheapest to get right in isolation,
 * same discipline as calendarDayGeometry.ts's vertical positioning. No
 * React; operates on real UTC instants (`startsAt`/`endsAt`), so overlap is
 * a plain temporal fact independent of which day/timezone a caller is
 * rendering the event within.
 */
import type { CalendarEvent } from '../../../types/calendar';
import { MIN_VISIBLE_MINUTES } from './calendarDayGeometry';

export interface EventLane {
  event: CalendarEvent;
  /** 0-indexed column within this event's overlap cluster. */
  lane: number;
  /** Total columns needed by this event's overlap cluster — scoped to the
   * cluster, not the whole day, so an empty morning isn't squeezed into a
   * wide grid just because the afternoon has a 3-way collision. */
  laneCount: number;
}

interface TimedEntry {
  event: CalendarEvent;
  startMs: number;
  endMs: number;
}

function toTimedEntry(event: CalendarEvent): TimedEntry {
  const startMs = new Date(event.startsAt).getTime();
  const rawEndMs = new Date(event.endsAt).getTime();
  // Same floor calendarDayGeometry.ts applies for visual sizing: overlap is
  // decided on the span that is actually DRAWN, so two 15-minute events
  // back to back (each floored to 30 visible minutes) split into lanes
  // instead of the second painting over the first.
  const endMs = Math.max(rawEndMs, startMs + MIN_VISIBLE_MINUTES * 60_000);
  return { event, startMs, endMs };
}

/**
 * Greedy interval-graph-coloring: sorted by start time, each event reuses
 * the first lane whose most-recently-placed event has already ended by this
 * event's start; only opens a new lane when every existing one is still
 * occupied. This is what lets two events that don't actually touch share a
 * lane even when a THIRD event overlaps both of them (the classic chain: A
 * overlaps B, B overlaps C, A and C don't) — a naive "give the whole
 * connected group one lane per event" approach would over-narrow the
 * columns for every event in the group.
 */
export function assignOverlapLanes(events: CalendarEvent[]): EventLane[] {
  const entries = events
    .map(toTimedEntry)
    .sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs);

  // Connected-component clustering via a sweep: a new cluster starts only
  // once every event in the running cluster has ended before the next
  // event begins. Scoping laneCount to the cluster (not the whole day) is
  // why an empty morning stays 1 lane wide next to a busy afternoon.
  const clusters: TimedEntry[][] = [];
  let clusterEnd = Number.NEGATIVE_INFINITY;
  for (const entry of entries) {
    if (clusters.length === 0 || entry.startMs >= clusterEnd) {
      clusters.push([entry]);
    } else {
      clusters[clusters.length - 1].push(entry);
    }
    clusterEnd = Math.max(clusterEnd, entry.endMs);
  }

  const result: EventLane[] = [];
  for (const cluster of clusters) {
    const laneEnds: number[] = [];
    // Carries the lane on the pair itself instead of a side Map keyed by
    // entry — the second loop then needs no lookup (and no non-null
    // assertion to claim it always hits), because `lane` was already known
    // at the moment each entry was placed.
    const placed: Array<{ entry: TimedEntry; lane: number }> = [];

    for (const entry of cluster) {
      let lane = laneEnds.findIndex((end) => end <= entry.startMs);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(entry.endMs);
      } else {
        laneEnds[lane] = entry.endMs;
      }
      placed.push({ entry, lane });
    }

    const laneCount = laneEnds.length;
    for (const { entry, lane } of placed) {
      result.push({ event: entry.event, lane, laneCount });
    }
  }

  return result;
}
